import type {
  FundBusinessForLocalGovernmentModel,
  SubProjectForKSModel,
} from '@/features/project/projects/_models/sub-project';
import { convertToNegative, formatValueByUnit } from '@/utils';
import type { z } from 'zod';

// setoff = 打ち消し
type Status = 'null' | 'zero' | 'setoff' | 'hasValue';
type FundBusinessForLocalGovernmentType = z.infer<
  typeof FundBusinessForLocalGovernmentModel
>;

export const getStatus = (
  value: number | null,
  negativeCount: number | null | undefined,
): Status => {
  if (value) {
    return 'hasValue';
  }
  if (value === null) {
    return 'null';
  }
  if (value === 0 && !negativeCount) {
    return 'zero';
  }
  return 'setoff';
};

export const getSumOfTheEndOfThePriorFundBalance = ({
  fundBusinessForLocalGovernment,
}: {
  fundBusinessForLocalGovernment: FundBusinessForLocalGovernmentType;
}) => {
  const totalAmountTarget: {
    key:
      | 'beginningOfTheFundBalance'
      | 'incomeAmount'
      | 'businessExpense'
      | 'amountReturnedToTheNationalTreasury';
    value: number | null;
    status: Status;
  }[] = [
    {
      // n-1基金残高
      key: 'beginningOfTheFundBalance',
      value: fundBusinessForLocalGovernment.beginningOfTheFundBalance,
      status: getStatus(
        fundBusinessForLocalGovernment.beginningOfTheFundBalance,
        fundBusinessForLocalGovernment.negativeBeginningOfTheFundBalanceCount,
      ),
    },
    {
      // n年度収入総額
      key: 'incomeAmount',
      value: fundBusinessForLocalGovernment.incomeAmount,
      status: getStatus(
        fundBusinessForLocalGovernment.incomeAmount,
        fundBusinessForLocalGovernment.negativeIncomeAmountCount,
      ),
    },
    {
      // n年度支出
      key: 'businessExpense',
      value: convertToNegative(fundBusinessForLocalGovernment.businessExpense),
      status: getStatus(
        fundBusinessForLocalGovernment.businessExpense,
        fundBusinessForLocalGovernment.negativeBusinessExpenseCount,
      ),
    },
    {
      // n年度国庫返納額
      key: 'amountReturnedToTheNationalTreasury',
      value: convertToNegative(
        fundBusinessForLocalGovernment.amountReturnedToTheNationalTreasury,
      ),
      status: getStatus(
        fundBusinessForLocalGovernment.amountReturnedToTheNationalTreasury,
        fundBusinessForLocalGovernment.negativeAmountReturnedToTheNationalTreasuryCount,
      ),
    },
  ];

  const isNoValue = totalAmountTarget.every((v) => v.status !== 'hasValue');
  const isAllNull = totalAmountTarget.every((v) => v.status === 'null');
  const isAllSetoff = totalAmountTarget.every((v) => v.status === 'setoff');
  const isSetoffAndNull = totalAmountTarget.every(
    (v) => v.status === 'setoff' || v.status === 'null',
  );
  if (isNoValue) {
    if (isAllNull) {
      return {
        totalAmount: null,
        negativeCount: 0,
      };
    }
    if (isAllSetoff || isSetoffAndNull) {
      return {
        totalAmount: 0,
        negativeCount: 1,
      };
    }

    // 全てが0の場合。
    // nullと0の場合。
    // 打ち消しと0の場合。
    // 打ち消しと0とnullがある場合。
    return {
      totalAmount: 0,
      negativeCount: 0,
    };
  }

  // 以下純粋な計算を行う

  const totalAmount = totalAmountTarget.reduce<number | null>((acc, curr) => {
    if (curr.value === null) {
      return acc;
    }
    return (acc ?? 0) + (curr.value ?? 0);
  }, null);

  const negativeCount = [
    totalAmountTarget.some((v) => v.value && v.value < 0) ? 1 : 0,
    fundBusinessForLocalGovernment.negativeBeginningOfTheFundBalanceCount,
    fundBusinessForLocalGovernment.negativeIncomeAmountCount,
  ].reduce((acc, curr) => {
    return (acc ?? 0) + (curr ?? 0);
  }, 0);

  return {
    totalAmount,
    negativeCount,
  };
};

export const executionProgressFormatAmount = ({
  value,
  negativeValueCount,
  isDebugMode,
}: {
  value: number | null | undefined;
  negativeValueCount: number | null | undefined;
  isDebugMode: boolean;
}) => {
  return formatValueByUnit(value, {
    operation: 'round',
    unit: 'thousand',
    showUnitLabel: false,
    isDebugMode,
    negativeCount: negativeValueCount,
  });
};

export const getPreviousFundBalanceColumnData = ({
  isSimpleResponse,
  isDebugMode,
  targetValue,
  targetNegativeValueCount,
  priorSubProjects,
  priorId,
  type,
}: {
  isSimpleResponse: boolean;
  isDebugMode: boolean;
  targetValue: number | null | undefined;
  targetNegativeValueCount: number | null | undefined;
  priorSubProjects: z.infer<typeof SubProjectForKSModel>[] | undefined;
  priorId: string | null;
  type: 'sum' | 'nationalExpense';
}): {
  value: string;
  showWarning?: boolean;
  result: 'notPriorSubProject' | 'simpleResponse' | 'normal';
} => {
  if (isSimpleResponse) {
    return {
      result: 'simpleResponse',
      value: executionProgressFormatAmount({
        value: targetValue,
        negativeValueCount: targetNegativeValueCount,
        isDebugMode,
      }),
    };
  }
  const priorSubProject = priorSubProjects?.find(
    (subProject) => subProject.id === priorId,
  );

  const returnValue = executionProgressFormatAmount({
    value: targetValue,
    negativeValueCount: targetNegativeValueCount,
    isDebugMode,
  });

  if (!priorSubProject?.fundBusinessForLocalGovernment) {
    return { value: returnValue, result: 'notPriorSubProject' };
  }

  if (type === 'nationalExpense') {
    return {
      value: returnValue,
      showWarning:
        priorSubProject.fundBusinessForLocalGovernment
          .nationalEquivalentOfTheEndOfTheFundBalance !== targetValue,
      result: 'normal',
    };
  }

  const { totalAmount, negativeCount } = getSumOfTheEndOfThePriorFundBalance({
    fundBusinessForLocalGovernment:
      priorSubProject.fundBusinessForLocalGovernment,
  });

  const formattedSumOfTheEndOfThePriorFundBalance =
    totalAmount === 0 && !!negativeCount ? null : totalAmount;

  return {
    value: returnValue,
    showWarning: targetValue !== formattedSumOfTheEndOfThePriorFundBalance,
    result: 'normal',
  };
};

export const getSimpleAmountColumnData = ({
  value,
  negativeValueCount,
  isDebugMode,
}: {
  value: number | null | undefined;
  negativeValueCount: number | null | undefined;
  isDebugMode: boolean;
}) => {
  return executionProgressFormatAmount({
    value,
    negativeValueCount,
    isDebugMode,
  });
};
